import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Header from '@amzn/awsui-components-console/header';
import Alert from '@amzn/awsui-components-console/alert';
import Box from '@amzn/awsui-components-console/box';
import Badge from '@amzn/awsui-components-console/badge';
import Grid from '@amzn/awsui-components-console/grid';
import Button from '@amzn/awsui-components-console/button';
import Spinner from '@amzn/awsui-components-console/spinner';
import { useDescribeStringQuery } from 'src/generated/graphql';
import { validateStringDetailParams } from './validation';
import SourceTextPanel from './components/SourceTextPanel';
import MetaBox from './components/MetaBox';
import TranslationProgressPanel from 'src/features/string-group-detail/components/TranslationProgressPanel';
import VariantsTable from './components/VariantsTable';
import CreateVariantModal from './components/CreateVariantModal';
import LocalesTable from './components/LocalesTable';
import VersionHistoryPanel from './components/VersionHistoryPanel';
import { isLangHubClient } from 'src/features/string-group-detail/utils/clientIdMapper';
import {
  buildNamespacePath,
  buildStringGroupPath,
  buildStringPath,
} from 'src/common/routes';
import { getDraft } from 'src/features/string-group-detail/utils/draftStorage';
import { useNamespacePermissions } from 'src/common/hooks/useNamespacePermissions';
import { DraftState } from 'src/features/string-group-detail/types';
import { findDraftEntry, isDraftEntryModified } from './utils/draftModified';
import { EditDraftContext } from './utils/editDraft';
import { computeAvailableCxIds } from './utils/availableCxIds';
import { computePendingVariants } from './utils/pendingVariants';
import StackedBreadcrumbGroup from 'src/common/ui/StackedBreadcrumbGroup';
import AccessDeniedAlert from 'src/common/authz/AccessDeniedAlert';
import {
  getAccessDeniedMessage,
  getAccessDeniedNamespace,
  isAccessDenied,
} from 'src/common/authz/isAccessDenied';
import { isResourceNotFound } from 'src/api/graphqlErrorType';
import { useTranslation } from 'src/i18n/shim/useTranslation';

const StringDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { namespaceId, stringGroupId, stringId } = useParams<{
    namespaceId: string;
    stringGroupId: string;
    stringId: string;
  }>();
  const [searchParams] = useSearchParams();
  const variant = searchParams.get('variant');
  const versionParam = searchParams.get('version');

  // Version is a String type in GraphQL (package convention for Long values)
  const parsedVersion = versionParam ?? undefined;

  // Route params are typed optional by react-router. Normalize once so the
  // queries, links, draft identity and version history all agree on the value.
  const resolvedNamespaceId = namespaceId || '';

  // SOURCE_MANAGEMENT governs authoring here exactly as it does on the group page.
  // This page is reachable by URL, so it cannot rely on the group page's gating.
  const permissions = useNamespacePermissions(resolvedNamespaceId);
  const resolvedStringGroupId = stringGroupId || '';
  const resolvedStringId = stringId || '';
  const cxIdFilter = variant || undefined;

  const [versionHistoryVisible, setVersionHistoryVisible] = useState(false);
  const [createVariantVisible, setCreateVariantVisible] = useState(false);
  /**
   * Server variant opened for editing from the Manage Variants table; also
   * drives the shared modal's visibility.
   */
  const [editingVariant, setEditingVariant] = useState<{
    cxId: string;
    sourceText: string;
    contextNote?: string | null;
  } | null>(null);

  /**
   * The group's whole pending draft, if any. Read from localStorage — which no
   * React state observes — so it is pulled on mount and re-pulled whenever this
   * page writes the draft (`refreshDraft`), which keeps the "modified" badge,
   * the modal's prefill and the group-level CX ID list honest.
   *
   * The WHOLE draft is retained rather than just this string's entry because the
   * same changeset also carries group-level edits (`groupEdits`), and the set of
   * CX IDs a variant may be created for depends on them — see `effectiveCxIds`.
   */
  const [draft, setDraft] = useState<DraftState | null>(null);

  /**
   * Re-reads the group's draft from storage.
   *
   * Keyed on the namespace and group alone because that is exactly the draft's
   * own storage key — a change of `stringId` cannot make the cached draft the
   * wrong one, and `draftEntry` below re-derives from it regardless.
   */
  const refreshDraft = useCallback(() => {
    setDraft(getDraft(resolvedNamespaceId, resolvedStringGroupId));
  }, [resolvedNamespaceId, resolvedStringGroupId]);

  useEffect(() => {
    refreshDraft();
  }, [refreshDraft]);

  /**
   * This string's pending entry in the draft, if any.
   *
   * Derived rather than stored so there is a single source of truth: one
   * `setDraft` per read keeps the entry and the group-level edits from ever
   * describing different draft revisions. Memoised on the same referentially
   * stable state the previous `draftEntry` state held, so consumers see an
   * unchanged identity across re-renders and nothing that feeds a query is
   * destabilised.
   */
  const draftEntry = useMemo(
    () => findDraftEntry(draft, resolvedStringId),
    [draft, resolvedStringId],
  );

  // Prefer the draft's pending edit for this CX ID over the server row, so
  // re-editing does not show stale values.
  const handleEditVariantClick = useCallback(
    (row: {
      cxId: string;
      sourceText: string;
      contextNote?: string | null;
    }) => {
      const pendingVariant = draftEntry?.variants.find(
        (v) => v.cxId === row.cxId,
      );
      setEditingVariant(pendingVariant ?? row);
    },
    [draftEntry],
  );

  // Client-side validation
  const validationErrors = validateStringDetailParams(t, {
    namespaceId,
    stringGroupId,
    stringId,
    variant,
    version: versionParam,
  });

  const hasValidationErrors = validationErrors.length > 0;

  // Only fire query if validation passes
  const { data, loading, error } = useDescribeStringQuery({
    variables: {
      namespaceId: resolvedNamespaceId,
      stringGroupId: resolvedStringGroupId,
      stringId: resolvedStringId,
      cxId: cxIdFilter,
      stringGroupVersion: parsedVersion,
    },
    skip: hasValidationErrors,
  });

  // When in variant view, fetch the base string separately to display the base
  // source text for comparison. The variant-filtered response only returns the
  // resolved (variant) text — the base text requires a second call without cxId.
  // Design decision: we pass the same stringGroupVersion to the base-string
  // comparison call so both primary and base views reflect the same historical
  // snapshot. This ensures consistent data when viewing a pinned version.
  // The same response also supplies the base snapshot the edit draft needs, so
  // its `error` is load-bearing rather than cosmetic — see `baseSnapshot` below.
  const isVariantView = !!variant;
  const {
    data: baseData,
    loading: baseLoading,
    error: baseError,
    refetch: refetchBase,
  } = useDescribeStringQuery({
    variables: {
      namespaceId: resolvedNamespaceId,
      stringGroupId: resolvedStringGroupId,
      stringId: resolvedStringId,
      cxId: undefined,
      stringGroupVersion: parsedVersion,
    },
    skip: hasValidationErrors || !isVariantView,
  });

  const namespacePath = buildNamespacePath(resolvedNamespaceId);
  const stringGroupPath = buildStringGroupPath(
    resolvedNamespaceId,
    resolvedStringGroupId,
  );
  const basePath = buildStringPath(
    resolvedNamespaceId,
    resolvedStringGroupId,
    resolvedStringId,
  );

  // StackedBreadcrumbGroup prepends its own root item and renders the last
  // entry as inert text, so no root entry is listed here and the trailing item
  // needs no href.
  const breadcrumbItems = [
    {
      topText: t('namespace-breadcrumb-label'),
      bottomText: resolvedNamespaceId,
      href: namespacePath,
    },
    {
      topText: t('breadcrumb-string-group'),
      bottomText: resolvedStringGroupId,
      href: stringGroupPath,
    },
  ];

  if (variant) {
    breadcrumbItems.push({
      topText: t('breadcrumb-string-detail'),
      bottomText: resolvedStringId,
      href: basePath,
    });
    breadcrumbItems.push({
      topText: t('breadcrumb-variant'),
      bottomText: variant,
      href: '',
    });
  } else {
    breadcrumbItems.push({
      topText: t('breadcrumb-string-detail'),
      bottomText: resolvedStringId,
      href: '',
    });
  }

  // Inner page content rendering — chrome (TopNav/LeftNav) is provided by AppShell
  const renderContent = () => {
    // Validation error state
    if (hasValidationErrors) {
      return (
        <SpaceBetween size="l">
          <div data-testid="breadcrumb">
            <StackedBreadcrumbGroup items={breadcrumbItems} />
          </div>
          <Alert type="error" header={t('string-page-validation-error')}>
            <ul>
              {validationErrors.map((err) => (
                <li key={err.field}>{err.message}</li>
              ))}
            </ul>
          </Alert>
        </SpaceBetween>
      );
    }

    // Loading state
    if (loading) {
      return (
        <SpaceBetween size="l">
          <div data-testid="breadcrumb">
            <StackedBreadcrumbGroup items={breadcrumbItems} />
          </div>
          <Box textAlign="center" padding="xxl">
            <Spinner size="large" />
            <Box variant="p" margin={{ top: 's' }}>
              {t('string-page-loading')}
            </Box>
          </Box>
        </SpaceBetween>
      );
    }

    // Error state
    if (error) {
      /*
       * Checked BEFORE the absence check below, so a refusal is never relabelled "String not
       * found" when the truth is that this caller may not see it.
       */
      if (isAccessDenied(error)) {
        return (
          <SpaceBetween size="l">
            <div data-testid="breadcrumb">
              <StackedBreadcrumbGroup items={breadcrumbItems} />
            </div>
            <AccessDeniedAlert
              message={getAccessDeniedMessage(error)}
              deniedNamespace={getAccessDeniedNamespace(error)}
              data-testid="string-access-denied"
            />
          </SpaceBetween>
        );
      }

      // Keyed on the resolver's errorType, not the message text: the branches below pick
      // different resource ids, and service wording must never select copy.
      const notFound = isResourceNotFound(error);

      return (
        <SpaceBetween size="l">
          <div data-testid="breadcrumb">
            <StackedBreadcrumbGroup items={breadcrumbItems} />
          </div>
          <Alert
            type="error"
            header={
              notFound
                ? t('string-page-not-found-header')
                : t('string-page-error-header')
            }
          >
            {notFound
              ? t('string-page-not-found-body', {
                  stringId,
                  namespaceId,
                  stringGroupId,
                })
              : t('string-page-load-failed', { detail: error.message })}
          </Alert>
        </SpaceBetween>
      );
    }

    const stringDetail = data?.describeString;
    if (!stringDetail) {
      return (
        <SpaceBetween size="l">
          <div data-testid="breadcrumb">
            <StackedBreadcrumbGroup items={breadcrumbItems} />
          </div>
          <Alert type="warning" header={t('string-page-no-data')}>
            {t('string-page-no-data-body')}
          </Alert>
        </SpaceBetween>
      );
    }

    const isVariant = !!variant;
    // Canonical ownership check — shared with the string-group detail page so
    // every LangHub client identity ('langhub', 'langhub-cms', any casing) is
    // treated the same, in parity with the backend's SupportedClientValidator.
    const isLangHubOwned = isLangHubClient(stringDetail.clientId);
    // A pinned historical version is a read-only view: an edit must always
    // branch from the current version, so editing is offered only on the
    // unpinned (latest) view.
    const isPinnedVersion = parsedVersion !== undefined;

    // The draft a source edit writes must always describe the BASE string, so
    // in variant view the base (no-cxId) response is the ONLY acceptable
    // source of truth. There is deliberately no fallback to `stringDetail`
    // here: that response is variant-resolved, so using it would persist the
    // variant's text as the base text and replace the string's real variant
    // list with a single-entry one built from the variant being viewed.
    // While it is missing (in flight, or the request failed) `draftContext`
    // stays undefined and SourceTextPanel offers no Edit action.
    const baseSnapshot = isVariant ? baseData?.describeString : stringDetail;

    const draftContext: EditDraftContext | undefined = baseSnapshot
      ? {
          namespaceId: resolvedNamespaceId,
          stringGroupId: resolvedStringGroupId,
          stringId: stringDetail.stringId,
          sourceLocale: stringDetail.sourceLocale,
          stringGroupVersion: stringDetail.stringGroupVersion,
          baseSourceText: baseSnapshot.sourceText,
          baseContextNote: baseSnapshot.contextNote,
          // The string's variants as the server reports them, in the draft's own
          // shape. Read from the base snapshot because the variant-resolved
          // response reports `variants: null`.
          variants: (baseSnapshot.variants ?? []).map((v) => ({
            cxId: v.cxId,
            sourceText: v.sourceText,
            contextNote: v.contextNote ?? undefined,
          })),
          inlineMarkupType: stringDetail.inlineMarkupType,
          messageFormatType: stringDetail.messageFormatType,
        }
      : undefined;

    /**
     * Whether this view has an unsubmitted change behind it.
     *
     * In variant view the answer depends on the server's variant list, and the
     * base snapshot is its only source — so the check is fed
     * `draftContext?.variants`, which is `undefined` for exactly as long as that
     * snapshot is (in flight, or the request failed). The badge then stays off
     * instead of comparing against an empty list it cannot trust, which would
     * flag every variant of a base-only edit. Base view is unaffected:
     * `baseSnapshot` is `stringDetail` there, which is already non-null.
     */
    const isModifiedInDraft = isDraftEntryModified(
      draftEntry,
      cxIdFilter,
      draftContext?.variants,
    );

    // One gate for every authoring control on this page: LangHub-owned source,
    // not a pinned historical version (an edit must branch from the current
    // one), and the namespace permission granted.
    const canAuthorEdit =
      isLangHubOwned && !isPinnedVersion && permissions.canManageSource;

    // Version history is offered on the base string only.
    const canViewVersionHistory = isLangHubOwned && !isVariant;

    // Variants the draft has but the server does not. One derivation feeding
    // both consumers below, so the table and the Create Variant dropdown cannot
    // disagree about what is pending. Both are `!isVariant`-gated, so
    // `stringDetail` is the base response with the server's real variant list.
    const pendingVariants = computePendingVariants(
      stringDetail.variants,
      draftEntry?.variants,
    );

    // The group's CX IDs as the PENDING draft would leave them, not as the
    // server still reports them. The same draft that holds this string's edits
    // can also hold group-level ones, and a variant only makes sense for a CX ID
    // the pending group actually has: a CX ID added in the draft must be
    // offerable, and one removed in the draft must not be, or the user could
    // author a variant for a CX ID the submission will not contain.
    //
    // `groupEdits.cxIds` is absent when the draft touched no CX IDs, which falls
    // back to the server list. An explicitly emptied list stays empty — `??`
    // only falls through on nullish, so `[]` is honoured and the dropdown
    // correctly offers nothing.
    const effectiveCxIds = draft?.groupEdits?.cxIds ?? stringDetail.cxIds;

    // CX IDs still free for a new variant. The draft's pending variants count as
    // taken too, or a second variant for the same CX ID would overwrite the first.
    const availableCxIds = computeAvailableCxIds(
      effectiveCxIds,
      stringDetail.variants,
      pendingVariants,
    );

    return (
      <SpaceBetween size="l">
        <div data-testid="breadcrumb">
          <StackedBreadcrumbGroup items={breadcrumbItems} />
        </div>

        <div data-testid="string-detail-title">
          <Header
            variant="h1"
            actions={
              canViewVersionHistory ? (
                <Button
                  variant="normal"
                  data-testid="version-history-toggle"
                  onClick={() =>
                    setVersionHistoryVisible(!versionHistoryVisible)
                  }
                >
                  {t('string-version-history')}
                </Button>
              ) : undefined
            }
          >
            <SpaceBetween size="xs" direction="horizontal" alignItems="center">
              {stringDetail.stringId}
              {isVariant ? (
                <Badge color="blue" data-testid="variant-badge">
                  Variant: {variant}
                </Badge>
              ) : (
                <Badge color="blue" data-testid="base-string-badge">
                  {t('string-base-string')}
                </Badge>
              )}
              {parsedVersion != null && !hasValidationErrors && (
                <Badge color="grey" data-testid="version-pin-badge">
                  {t('string-page-version-pin-badge', {
                    version: parsedVersion,
                  })}
                </Badge>
              )}
              {isModifiedInDraft && (
                <Badge color="blue" data-testid="draft-modified-badge">
                  {t('string-modified-pending')}
                </Badge>
              )}
            </SpaceBetween>
          </Header>
        </div>

        {/* Mounted only while open, which is what makes its per-version
            describeString calls lazy. On a pinned version it renders a short
            note instead of a comparison — the version-list API cannot express
            the window below an arbitrary version, so see its own doc. */}
        {canViewVersionHistory && versionHistoryVisible && (
          <VersionHistoryPanel
            namespaceId={resolvedNamespaceId}
            stringGroupId={resolvedStringGroupId}
            stringId={resolvedStringId}
            cxId={cxIdFilter}
            currentVersion={stringDetail.stringGroupVersion}
            isPinnedVersion={isPinnedVersion}
            onClose={() => setVersionHistoryVisible(false)}
          />
        )}

        {/* The base string backs both the comparison row and the edit draft, so
            when its request fails the failure is surfaced here rather than
            silently degrading — editing is withheld until a retry succeeds.
            A REFUSAL gets no Retry (the identical request would be refused
            again) and is checked first, because the generic framing below would
            call a permanent refusal an unavailability. */}
        {isVariant &&
          baseError &&
          (isAccessDenied(baseError) ? (
            <AccessDeniedAlert
              message={getAccessDeniedMessage(baseError)}
              deniedNamespace={getAccessDeniedNamespace(baseError)}
              data-testid="base-string-access-denied"
            />
          ) : (
            <Alert
              type="error"
              header={t('string-page-base-unavailable')}
              data-testid="base-string-error"
              action={
                <Button
                  data-testid="base-string-retry"
                  onClick={() => {
                    // Apollo surfaces the retry's own failure through `error`
                    // again, so the rejection is handled by the render path.
                    void refetchBase().catch(() => undefined);
                  }}
                >
                  {t('string-page-retry')}
                </Button>
              }
            >
              {t('string-page-base-load-failed', {
                detail: baseError.message,
              })}
            </Alert>
          ))}

        {/* Two-column layout: source text (~75%) + metadata (~25%) using Grid */}
        <Grid
          gridDefinition={[
            { colspan: { default: 12, s: 9 } },
            { colspan: { default: 12, s: 3 } },
          ]}
        >
          <SourceTextPanel
            sourceLocale={stringDetail.sourceLocale}
            sourceText={stringDetail.sourceText}
            contextNote={stringDetail.contextNote}
            variantCxId={cxIdFilter}
            baseSourceText={baseData?.describeString?.sourceText}
            baseSourceTextLoading={isVariant && baseLoading}
            stringId={stringDetail.stringId}
            draftContext={draftContext}
            draftEntry={draftEntry}
            onDraftSaved={refreshDraft}
            canEdit={canAuthorEdit}
            permissionsLoading={permissions.loading}
          />
          <MetaBox
            cxFamilyId={stringDetail.cxFamilyId}
            cxIds={stringDetail.cxIds}
            variantCxId={cxIdFilter}
            anchorId={stringDetail.anchorId}
          />
        </Grid>

        {/* Translation progress — shares the string-group detail page's panel
            so both pages present progress identically. That panel takes only
            the progress breakdown; the variant context is already conveyed by
            the page header's variant badge. */}
        <TranslationProgressPanel progress={stringDetail.translationProgress} />

        {/* Manage variants (only shown in base view) */}
        {!isVariant && (
          <VariantsTable
            variants={stringDetail.variants || []}
            pendingVariants={pendingVariants}
            baseUrl={basePath}
            versionParam={parsedVersion}
            canCreateVariant={canAuthorEdit}
            permissionsLoading={permissions.loading}
            availableCxIds={availableCxIds}
            onCreateVariantClick={() => setCreateVariantVisible(true)}
            onEditVariantClick={handleEditVariantClick}
          />
        )}

        {/* One modal for create and edit: both are draft writes, not API
            calls, so both use the source edit's draftContext. */}
        {!isVariant && draftContext && (
          <CreateVariantModal
            visible={createVariantVisible || !!editingVariant}
            stringId={stringDetail.stringId}
            availableCxIds={availableCxIds}
            draftContext={draftContext}
            editingVariant={editingVariant ?? undefined}
            onDismiss={() => {
              setCreateVariantVisible(false);
              setEditingVariant(null);
            }}
            onDraftSaved={refreshDraft}
          />
        )}

        {/* Locales table — Item 1: bottom spacing via Box margin */}
        <Box margin={{ bottom: 'l' }}>
          <LocalesTable
            translations={data?.describeString?.translations || []}
            loading={loading}
            error={undefined}
          />
        </Box>
      </SpaceBetween>
    );
  };

  return (
    <main id="main-content">
      <div style={{ maxWidth: 1860, margin: '0 auto', padding: 24 }}>
        {renderContent()}
      </div>
    </main>
  );
};

export default StringDetailPage;
